import { ReactNode, useEffect, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import type { LocationLike, NavigateFn } from './RequireAuth'

function AuthLoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50" aria-label="Loading">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
        <span>Loading…</span>
      </div>
    </div>
  )
}

type RequireCompanyInnerProps = {
  children: ReactNode
  loginPath?: string
  fallbackPath?: string
  location: LocationLike
  /** When set, redirects are done via this fn (useEffect) instead of <Navigate>. Use from app so Router context is correct. */
  navigate?: NavigateFn
}

/**
 * Inner guard for /company/*: requires customer token + me.has_company (from GET /auth/me).
 * Uses provided location and optional navigate to avoid Router context issues when the guard
 * runs with a different react-router instance (e.g. shared package).
 */
export function RequireCompanyInner({
  children,
  loginPath = '/',
  fallbackPath = '/dashboard',
  location,
  navigate: navigateFn,
}: RequireCompanyInnerProps) {
  const hasHydrated = useAuthStore((s) => s._hasHydrated)
  const customer = useAuthStore((s) => s.customer)
  const me = useAuthStore((s) => s.me)
  const didRedirect = useRef(false)

  const authenticated = !!customer?.token && !!customer?.user

  let redirectTo: string | null = null
  let redirectState: unknown = undefined
  if (!authenticated) {
    redirectTo = loginPath
    redirectState = { from: location }
  } else if (me != null && !me.has_company) {
    redirectTo = fallbackPath
  }

  useEffect(() => {
    if (!redirectTo || !navigateFn || didRedirect.current) return
    didRedirect.current = true
    navigateFn(redirectTo, { replace: true, state: redirectState })
  }, [redirectTo, navigateFn])

  if (!hasHydrated) return <AuthLoadingScreen />
  if (authenticated && me == null) return <AuthLoadingScreen />
  if (redirectTo) {
    if (navigateFn) return <AuthLoadingScreen />
    return <Navigate to={redirectTo} state={redirectState} replace />
  }
  return <>{children}</>
}

type Props = RequireCompanyInnerProps & { location?: LocationLike; navigate?: NavigateFn }

/**
 * Guard for /company/*: requires customer token + me.has_company.
 * When used in an app that shares the same react-router instance, you can omit location/navigate.
 * When used in an app with a separate react-router (e.g. shared package), pass location + navigate from the app.
 */
export function RequireCompany({ location: locationProp, navigate, ...rest }: Props) {
  const locationFromRouter = useLocation()
  const location = locationProp ?? locationFromRouter
  return <RequireCompanyInner {...rest} location={location} navigate={navigate} />
}
